/** SASE partners (SASE-35, P35c): server state (TanStack Query) and mutations. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import { type ActingFor, useActingForStore } from "@/lib/actingFor";
import { useInvalidatingMutation } from "@/lib/mutations";

import {
  acceptPartner,
  addMember,
  adminCreatePartner,
  adminListPartners,
  adminUpdatePartner,
  createInvite,
  deleteInvite,
  getDashboard,
  getPartner,
  getPartnerAccess,
  listInvites,
  listMembers,
  partnerActivity,
  releaseCustomer,
  removeMember,
  revokePartner,
  setBranding,
  updateMember,
  updatePartnerAccess,
} from "./api";
import type { PartnerBranding, PartnerRole } from "./types";

export const partnerKeys = {
  all: ["sase", "partner"] as const,
  me: () => [...partnerKeys.all, "me"] as const,
  dashboard: (page: number) => [...partnerKeys.all, "dashboard", page] as const,
  members: () => [...partnerKeys.all, "members"] as const,
  invites: () => [...partnerKeys.all, "invites"] as const,
  access: () => ["sase", "partner-access"] as const,
  activity: () => ["sase", "partner-access", "activity"] as const,
  admin: () => ["sase", "partner-admin"] as const,
};

const notFound = (error: unknown) => isAxiosError(error) && error.response?.status === 404;

/** The caller's partner account; `data` stays undefined (no error) when the
 *  caller's organisation is not a partner. */
export const usePartner = () =>
  useQuery({
    queryKey: partnerKeys.me(),
    queryFn: () => getPartner(),
    retry: (count, error) => !notFound(error) && count < 2,
  });

export const isNotAPartner = notFound;

export const usePartnerDashboard = (page: number, enabled: boolean) =>
  useQuery({
    queryKey: partnerKeys.dashboard(page),
    queryFn: () => getDashboard(page),
    enabled,
  });

export const usePartnerMembers = (enabled: boolean) =>
  useQuery({ queryKey: partnerKeys.members(), queryFn: () => listMembers(), enabled });

export const usePartnerInvites = (enabled: boolean) =>
  useQuery({ queryKey: partnerKeys.invites(), queryFn: () => listInvites(), enabled });

export const useSetBranding = () =>
  useInvalidatingMutation((body: PartnerBranding) => setBranding(body), {
    invalidates: [partnerKeys.all],
    successMessage: "Branding saved",
    errorMessage: "Could not save the branding",
  });

export const useAddMember = () =>
  useInvalidatingMutation(
    ({ email, role }: { email: string; role: PartnerRole }) => addMember(email, role),
    {
      invalidates: [partnerKeys.members()],
      successMessage: "Added to your partner team",
      errorMessage: "Could not add this person",
    },
  );

export const useUpdateMember = () =>
  useInvalidatingMutation(
    ({ id, role }: { id: string; role: PartnerRole }) => updateMember(id, role),
    {
      invalidates: [partnerKeys.members()],
      successMessage: "Role changed",
      errorMessage: "Could not change the role",
    },
  );

export const useRemoveMember = () =>
  useInvalidatingMutation((id: string) => removeMember(id), {
    invalidates: [partnerKeys.members()],
    successMessage: "Removed from your partner team",
    errorMessage: "Could not remove this person",
  });

export const useCreateInvite = () =>
  useInvalidatingMutation(
    (body: { role: PartnerRole; note?: string; valid_days: number }) => createInvite(body),
    { invalidates: [partnerKeys.invites()], errorMessage: "Could not make the invitation" },
  );

export const useDeleteInvite = () =>
  useInvalidatingMutation((id: string) => deleteInvite(id), {
    invalidates: [partnerKeys.invites()],
    successMessage: "Invitation withdrawn",
    errorMessage: "Could not withdraw the invitation",
  });

export const useReleaseCustomer = () =>
  useInvalidatingMutation((orgId: string) => releaseCustomer(orgId), {
    invalidates: [partnerKeys.all],
    successMessage: "You no longer manage this customer",
    errorMessage: "Could not stop managing this customer",
  });

// ---- managing a customer ------------------------------------------------------
/** Start or stop managing a customer: every cached answer belonged to the other
 *  organisation, so the cache is emptied both ways. */
export function useActingFor() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { acting, start, stop } = useActingForStore();
  return {
    acting,
    manage: (target: ActingFor) => {
      start(target);
      queryClient.removeQueries();
      navigate("/sase");
    },
    stopManaging: () => {
      stop();
      queryClient.removeQueries();
      navigate("/sase/settings/partner");
    },
  };
}

// ---- the customer's page about its partner ------------------------------------
export const usePartnerAccess = () =>
  useQuery({ queryKey: partnerKeys.access(), queryFn: () => getPartnerAccess() });

export const usePartnerActivity = (enabled: boolean) =>
  useQuery({ queryKey: partnerKeys.activity(), queryFn: () => partnerActivity(), enabled });

export const useAcceptPartner = () =>
  useInvalidatingMutation(
    ({ code, role }: { code: string; role: PartnerRole }) => acceptPartner(code, role),
    {
      invalidates: [partnerKeys.access()],
      successMessage: "Your partner can now manage your organisation",
      errorMessage: "Could not accept the invitation",
    },
  );

export const useUpdatePartnerAccess = () =>
  useInvalidatingMutation((role: PartnerRole) => updatePartnerAccess(role), {
    invalidates: [partnerKeys.access()],
    successMessage: "Access changed",
    errorMessage: "Could not change your partner's access",
  });

export const useRevokePartner = () =>
  useInvalidatingMutation(() => revokePartner(), {
    invalidates: [partnerKeys.access()],
    successMessage: "Your partner's access is removed",
    errorMessage: "Could not remove your partner's access",
  });

// ---- the operator's -----------------------------------------------------------
export const useAdminPartners = () =>
  useQuery({ queryKey: partnerKeys.admin(), queryFn: () => adminListPartners() });

export const useAdminCreatePartner = () =>
  useInvalidatingMutation(
    (body: { org_id: string; admin_email: string; name?: string }) => adminCreatePartner(body),
    {
      invalidates: [partnerKeys.admin()],
      successMessage: "The organisation is now a partner",
      errorMessage: "Could not make the organisation a partner",
    },
  );

export const useAdminUpdatePartner = () =>
  useInvalidatingMutation(
    ({ id, status }: { id: string; status: "active" | "suspended" }) =>
      adminUpdatePartner(id, { status }),
    {
      invalidates: [partnerKeys.admin()],
      successMessage: "Partner updated",
      errorMessage: "Could not update the partner",
    },
  );
